import { z } from 'zod';
import {
  AuditSchema,
  DateOnlySchema,
  IdSchema,
  InstantSchema,
  VisibilitySchema,
} from './common.ts';

/**
 * Whether a date is worked, overriding the company working week.
 *
 * One vocabulary for one concept: the calendar maths in `work-calendar.ts`
 * speaks `working`/`nonWorking`, so the stored record does too. An exception
 * cuts both ways — closing a holiday and opening a Saturday push — which is
 * why this is a kind rather than a `holiday: boolean`.
 */
export const WORKDAY_KINDS = ['working', 'nonWorking'] as const;
export const WorkdayKindSchema = z.enum(WORKDAY_KINDS);
export type WorkdayKind = z.infer<typeof WorkdayKindSchema>;

/**
 * Why the day was changed. Carried into the shift history when an exception
 * moves dates, so the baseline tab can answer "why did this job slip" without
 * anyone retyping it.
 */
export const WORKDAY_EXCEPTION_REASONS = [
  'holiday',
  'weather',
  'companyEvent',
  'subAvailability',
  'inspection',
  'pushDay',
  'other',
] as const;
export const WorkdayExceptionReasonSchema = z.enum(WORKDAY_EXCEPTION_REASONS);
export type WorkdayExceptionReason = z.infer<typeof WorkdayExceptionReasonSchema>;

/**
 * A single date on one job's calendar that does not follow the working week.
 *
 * The document id is the date itself rather than a ULID. A date can only have
 * one ruling, and making the key carry that means two people adding the same
 * holiday converge on one record instead of racing to create two conflicting
 * ones. It stays a client-generated, database-portable key, so the reason the
 * rest of the model uses ULIDs still holds.
 */
export const WorkdayExceptionSchema = AuditSchema.extend({
  /** Equal to `date`. See the note above. */
  id: DateOnlySchema,
  companyId: IdSchema,
  jobId: IdSchema,
  date: DateOnlySchema,
  kind: WorkdayKindSchema,
  reason: WorkdayExceptionReasonSchema,
  note: z.string().max(500).optional(),
}).refine((exception) => exception.id === exception.date, {
  message: 'A workday exception is stored under its own date',
  path: ['id'],
});
export type WorkdayException = z.infer<typeof WorkdayExceptionSchema>;

/**
 * Named display colours for schedule bars.
 *
 * The *name* is stored, never a hex value: the palette can be restyled, or a
 * dark theme added, without rewriting every stored item, and a name survives
 * a move to a different database. `packages/tokens` maps names to colours.
 */
export const SCHEDULE_COLOURS = [
  'slate',
  'blue',
  'green',
  'amber',
  'red',
  'violet',
  'teal',
] as const;
export const ScheduleColourSchema = z.enum(SCHEDULE_COLOURS);
export type ScheduleColour = z.infer<typeof ScheduleColourSchema>;

/** Who is doing the work: an internal user, or a sub from the directory. */
export const AssigneeSchema = z.object({
  kind: z.enum(['user', 'contact']),
  id: IdSchema,
  /** Whether this assignee is notified when the item changes. */
  notify: z.boolean().default(true),
});
export type Assignee = z.infer<typeof AssigneeSchema>;

export const REMINDERS = ['none', '1d', '2d', '3d', '1w'] as const;
export const ReminderSchema = z.enum(REMINDERS);
export type Reminder = z.infer<typeof ReminderSchema>;

/** Notes written for one audience, rendered only to that audience. */
export const ScheduleNotesSchema = z.object({
  internal: z.string().max(5000).optional(),
  subs: z.string().max(5000).optional(),
  owner: z.string().max(5000).optional(),
});
export type ScheduleNotes = z.infer<typeof ScheduleNotesSchema>;

export const DEPENDENCY_TYPES = ['FS', 'SS', 'FF', 'SF'] as const;
export const DependencyTypeSchema = z.enum(DEPENDENCY_TYPES);
export type DependencyType = z.infer<typeof DependencyTypeSchema>;

/**
 * A constraint on when this item may start or finish, relative to another.
 *
 * Stored here rather than as its own collection because a predecessor has no
 * identity of its own and is only ever read with its successor. The cascade
 * that enforces these lands with the dependency work.
 */
export const PredecessorSchema = z.object({
  itemId: IdSchema,
  type: DependencyTypeSchema,
  /** May be negative: "start three working days before the predecessor ends". */
  lagDays: z.number().int(),
});
export type Predecessor = z.infer<typeof PredecessorSchema>;

/** An item that runs to the clock rather than filling whole days. */
export const HourlyWindowSchema = z.object({
  startTime: z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Expected a time as HH:MM'),
  endTime: z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Expected a time as HH:MM'),
});
export type HourlyWindow = z.infer<typeof HourlyWindowSchema>;

/**
 * Why a date moved.
 *
 * A fixed list, because the point of recording a reason is to be able to
 * answer "why is this job late" across a whole schedule — and free text
 * cannot be counted. `note` carries the detail that does not fit a category.
 */
export const SHIFT_REASONS = [
  'weather',
  'materialDelay',
  'ownerDecision',
  'subAvailability',
  'inspection',
  'workdayException',
  'other',
] as const;
export const ShiftReasonSchema = z.enum(SHIFT_REASONS);
export type ShiftReason = z.infer<typeof ShiftReasonSchema>;

/**
 * One recorded movement of an item's dates.
 *
 * `cascade` separates "somebody moved this" from "this moved because
 * something it depends on moved". Both are real, but only the first is a
 * decision anyone made, and a slip report that conflates them blames the
 * wrong work.
 */
export const ScheduleShiftRecordSchema = z.object({
  at: InstantSchema,
  byUid: IdSchema,
  fromStart: DateOnlySchema,
  toStart: DateOnlySchema,
  fromEnd: DateOnlySchema,
  toEnd: DateOnlySchema,
  reason: ShiftReasonSchema.optional(),
  note: z.string().max(1000).optional(),
  cascade: z.boolean(),
});
export type ScheduleShiftRecord = z.infer<typeof ScheduleShiftRecordSchema>;

export const CONFIRMATION_STATUSES = ['pending', 'accepted', 'declined'] as const;
export const ConfirmationStatusSchema = z.enum(CONFIRMATION_STATUSES);
export type ConfirmationStatus = z.infer<typeof ConfirmationStatusSchema>;

/**
 * A subcontractor's answer to the dates they have been given.
 *
 * Keyed by contact id on the item rather than held in its own collection:
 * there is exactly one answer per sub per item, it is only ever read with
 * the item, and a separate collection would need its own rules for the one
 * write a sub is allowed to make anywhere in the system.
 */
export const ConfirmationSchema = z.object({
  status: ConfirmationStatusSchema,
  /** When the sub answered, or when the request was sent while pending. */
  at: InstantSchema,
  note: z.string().max(1000).optional(),
});
export type Confirmation = z.infer<typeof ConfirmationSchema>;

/**
 * The dates an item was expected to run on, captured at a point in time.
 *
 * A baseline is the answer to "what did we say we would do", kept so that
 * slip can be reported against a commitment rather than against whatever the
 * schedule happened to say yesterday. Taken deliberately, not on every edit:
 * a baseline that moved with the work would always show zero slip.
 */
export const ScheduleBaselineSchema = z.object({
  startDate: DateOnlySchema,
  endDate: DateOnlySchema,
  workDays: z.number().int().min(1),
  setAt: InstantSchema,
  setBy: IdSchema,
});
export type ScheduleBaseline = z.infer<typeof ScheduleBaselineSchema>;

/**
 * One piece of work on a job's schedule.
 *
 * `endDate` is derived from `startDate`, `workDays` and the job calendar on
 * every write, never accepted from a client. It is stored rather than
 * computed on read so that the calendar, list and Gantt views can all query
 * and sort by it, and so a date range query does not have to load the whole
 * schedule to find out what falls inside it.
 */
export const ScheduleItemSchema = AuditSchema.extend({
  id: IdSchema,
  companyId: IdSchema,
  jobId: IdSchema,
  title: z.string().min(1).max(200),
  colour: ScheduleColourSchema,
  phaseId: IdSchema.optional(),
  tagIds: z.array(IdSchema).default([]),
  assignees: z.array(AssigneeSchema).default([]),
  /**
   * Plant booked onto this work (SCRUM-211).
   *
   * **Ids on the item, not a booking record with dates of its own.** The
   * item's `startDate` and `endDate` *are* the booking, so dragging the
   * bar moves the plant with it and there is no parallel range left to
   * drift. The cross-job mirror that answers "where is EX-04 next Monday"
   * is derived from this by a trigger — see `equipment-booking.ts`.
   */
  equipmentIds: z.array(IdSchema).default([]),
  /**
   * Materials this work needs (SCRUM-210).
   *
   * A list of what to have on site, **not a purchase and not a cost**.
   * Ordering is what a purchase order is for and costing is what the
   * budget is for; an item that quietly created either would make a
   * schedule edit a financial act. This is the foreman's list.
   */
  materials: z
    .array(
      z.object({
        catalogItemId: IdSchema,
        quantityThousandths: z.number().int().min(0).default(1_000),
      }),
    )
    .max(200)
    .default([]),
  startDate: DateOnlySchema,
  workDays: z.number().int().min(1),
  endDate: DateOnlySchema,
  hourly: HourlyWindowSchema.optional(),
  progress: z.number().int().min(0).max(100).default(0),
  completed: z.boolean().default(false),
  completedAt: InstantSchema.optional(),
  completedBy: IdSchema.optional(),
  reminder: ReminderSchema.default('none'),
  predecessors: z.array(PredecessorSchema).default([]),
  notes: ScheduleNotesSchema.default({}),
  visibility: VisibilitySchema,
  baseline: ScheduleBaselineSchema.optional(),
  confirmations: z.record(IdSchema, ConfirmationSchema).default({}),
  /**
   * Most recent movements first, capped at `MAX_SHIFT_HISTORY`.
   *
   * A window rather than the whole history: this array lives inside the item
   * document, and an unbounded one would grow towards Firestore's size limit
   * on a long job. The permanent record belongs in the job's append-only
   * audit log (`docs/DATA_MODEL.md` §2), which is not built yet.
   */
  shifts: z.array(ScheduleShiftRecordSchema).max(200).default([]),
});
export type ScheduleItem = z.infer<typeof ScheduleItemSchema>;

/**
 * Plant and materials on an item, **tolerating documents written before
 * those fields existed**.
 *
 * `equipmentIds` and `materials` were added on 2026-10-21 with a Zod
 * default, which made them required in the type and absent in every
 * document already stored. Nothing applies the default on read: the
 * Firestore adapter hands back what Firestore holds, so an item saved
 * before that day comes back with neither, and `item.materials.map(...)`
 * throws.
 *
 * That broke more than it looked: opening any existing schedule item,
 * saving a job's schedule as a template, applying a template written
 * earlier, and the whole materials page. Reported from dev — new items
 * opened and old ones did not, which is exactly the shape of this.
 *
 * Read through these rather than reaching for the field, so the next
 * array added to this entity does not repeat it. The same reasoning as
 * `portalShows`, which tolerates a job saved before a module existed.
 */
export function itemEquipmentIds(
  item: Pick<Partial<ScheduleItem>, 'equipmentIds'>,
): readonly string[] {
  return item.equipmentIds ?? [];
}

export function itemMaterials(
  item: Pick<Partial<ScheduleItem>, 'materials'>,
): readonly ScheduleItem['materials'][number][] {
  return item.materials ?? [];
}

/**
 * A grouping of schedule items, like "Foundation" or "Rough-in".
 *
 * Phases belong to the job rather than the company: two jobs rarely run the
 * same breakdown, and a shared list would be edited by whoever touched it
 * last. Company-wide reuse arrives as schedule templates.
 */
export const SchedulePhaseSchema = AuditSchema.extend({
  id: IdSchema,
  companyId: IdSchema,
  jobId: IdSchema,
  name: z.string().min(1).max(120),
  colour: ScheduleColourSchema,
  /** Sort position. Gaps are fine; only the relative order is meaningful. */
  order: z.number().int(),
});
export type SchedulePhase = z.infer<typeof SchedulePhaseSchema>;
